import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check, Upload, X, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, getUTM, trackLead, errMsg } from "@/lib/api";
import { Field, TextInput, TextArea, SelectField, Pills, Checkbox, DateField } from "./fields";
import { postcodeRe, validateContact } from "./validators";
import ThankYou from "./ThankYou";
import { toast } from "sonner";

const EMPTY = {
  postcode: "", huisnummer: "", toevoeging: "", street: "", city: "", lat: null, lon: null, manual: false,
  property_type: "", rooms: "", sqm: "", furnishing: "",
  rent: "", available_date: "", inschrijving: "", description: "", photos: [],
  name: "", phone: "+31 ", email: "", preferred_channel: "", consent: false,
};

export default function OwnerForm({ config }) {
  const { t, lang } = useLang();
  const [step, setStep] = useState(1);
  const [v, setV] = useState(() => {
    try {
      const d = localStorage.getItem("he_draft_owner");
      return d ? { ...EMPTY, ...JSON.parse(d) } : EMPTY;
    } catch {
      return EMPTY;
    }
  });
  const [errors, setErrors] = useState({});
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrFound, setAddrFound] = useState(!!v.street);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const debounceRef = useRef(null);

  const set = (patch) => setV((p) => ({ ...p, ...patch }));

  useEffect(() => {
    const { photos, ...rest } = v;
    localStorage.setItem("he_draft_owner", JSON.stringify(rest));
  }, [v]);

  // PDOK auto-complete
  useEffect(() => {
    if (v.manual) return;
    const pc = v.postcode.replace(/\s/g, "");
    if (!postcodeRe.test(v.postcode.trim()) || !v.huisnummer.trim()) {
      setAddrFound(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const r = await api.get("/address/lookup", { params: { postcode: pc, huisnummer: v.huisnummer } });
        if (r.data.found) {
          set({ street: r.data.street, city: r.data.city, lat: r.data.lat, lon: r.data.lon });
          setAddrFound(true);
        } else {
          setAddrFound(false);
        }
      } catch {
        setAddrFound(false);
      } finally {
        setAddrLoading(false);
      }
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.postcode, v.huisnummer, v.manual]);

  const uploadPhotos = async (files) => {
    const list = Array.from(files).slice(0, 5 - v.photos.length);
    setUploading(true);
    for (const file of list) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: 5MB+`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setV((p) => ({ ...p, photos: [...p.photos, { id: r.data.id, filename: r.data.filename }] }));
      } catch {
        toast.error("Fotoğraf yüklenemedi");
      }
    }
    setUploading(false);
  };

  const validateStep1 = () => {
    const e = {};
    if (!v.manual) {
      if (!postcodeRe.test(v.postcode.trim())) e.postcode = t.form.errors.postcode;
      if (!v.huisnummer.trim()) e.huisnummer = t.form.errors.required;
    }
    if (!v.street.trim()) e.street = t.form.errors.required;
    if (!v.city.trim()) e.city = t.form.errors.required;
    if (!v.property_type) e.property_type = t.form.errors.required;
    if (!v.rooms) e.rooms = t.form.errors.required;
    if (!v.furnishing) e.furnishing = t.form.errors.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep2 = () => {
    const e = {};
    if (!v.rent?.toString().trim()) e.rent = t.form.errors.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    const e = validateContact(v, t);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    try {
      const payload = {
        address: {
          postcode: v.postcode, huisnummer: v.huisnummer, toevoeging: v.toevoeging,
          street: v.street, city: v.city, lat: v.lat, lon: v.lon, manual: v.manual,
        },
        property_type: v.property_type, rooms: v.rooms, sqm: v.sqm, furnishing: v.furnishing,
        rent: v.rent, available_date: v.available_date, inschrijving: v.inschrijving,
        description: v.description, photos: v.photos.map((p) => p.id),
        name: v.name, phone: v.phone, email: v.email, preferred_channel: v.preferred_channel,
        consent: v.consent, meta: getUTM(), lang, website: "",
      };
      await api.post("/leads/owner", payload);
      trackLead("owner");
      localStorage.removeItem("he_draft_owner");
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <ThankYou type="owner" config={config} />;

  return (
    <div data-testid="owner-form">
      <StepBar step={step} />
      <div className="mb-6 flex items-start gap-2 rounded-xl bg-mist border border-brand/30 p-3.5 text-sm">
        <ShieldCheck size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand" />
        <span className="text-ink/80">{t.form.ownerReminder}</span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.3 }} className="space-y-5">
          {step === 1 && (
            <>
              <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-stone">{t.form.steps.property}</h4>
              {!v.manual && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t.form.labels.postcode} error={errors.postcode}>
                      <TextInput value={v.postcode} onChange={(x) => set({ postcode: x.toUpperCase() })} placeholder="1043 BA" error={errors.postcode} data-testid="owner-postcode" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label={t.form.labels.huisnummer} error={errors.huisnummer}>
                        <TextInput value={v.huisnummer} onChange={(x) => set({ huisnummer: x })} placeholder="4" error={errors.huisnummer} data-testid="owner-huisnummer" />
                      </Field>
                      <Field label={t.form.labels.toevoeging}>
                        <TextInput value={v.toevoeging} onChange={(x) => set({ toevoeging: x })} placeholder="A" />
                      </Field>
                    </div>
                  </div>
                  {addrLoading && (
                    <div className="flex items-center gap-2 text-sm text-stone"><Loader2 size={16} className="animate-spin" /> …</div>
                  )}
                  {addrFound && v.street && (
                    <div className="flex items-center gap-2 rounded-md bg-mist border border-line p-3 text-sm" data-testid="owner-address-confirm">
                      <Check size={16} className="text-moss shrink-0" />
                      <span>{v.street} {v.huisnummer}{v.toevoeging}, {v.postcode} {v.city} — {t.form.labels.addressConfirm}</span>
                    </div>
                  )}
                </>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={t.form.labels.street} error={errors.street}>
                  <TextInput value={v.street} onChange={(x) => set({ street: x })} error={errors.street} />
                </Field>
                <Field label={t.form.labels.city} error={errors.city}>
                  <TextInput value={v.city} onChange={(x) => set({ city: x })} error={errors.city} />
                </Field>
              </div>
              <button type="button" onClick={() => set({ manual: !v.manual })} className="text-sm text-ink underline underline-offset-4 hover:opacity-70" data-testid="owner-manual-toggle">
                {t.form.labels.manualToggle}
              </button>
              <p className="text-xs text-stone flex items-start gap-1.5"><ShieldCheck size={13} className="mt-0.5 shrink-0" />{t.form.addressPrivacy}</p>

              <Field label={t.form.labels.propertyType} error={errors.property_type}>
                <Pills value={v.property_type} onChange={(x) => set({ property_type: x })} options={t.form.options.propertyTypeOwner} testId="owner-type" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={t.form.labels.rooms} error={errors.rooms}>
                  <SelectField value={v.rooms} onChange={(x) => set({ rooms: x })} options={["1", "2", "3", "4", "5", "6+"]} placeholder="—" error={errors.rooms} testId="owner-rooms" />
                </Field>
                <Field label={t.form.labels.sqm}>
                  <TextInput type="number" value={v.sqm} onChange={(x) => set({ sqm: x })} placeholder="75" />
                </Field>
              </div>
              <Field label={t.form.labels.furnishing} error={errors.furnishing}>
                <Pills value={v.furnishing} onChange={(x) => set({ furnishing: x })} options={t.form.options.furnishing} testId="owner-furnishing" />
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-stone">{t.form.steps.rental}</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={t.form.labels.rent} error={errors.rent}>
                  <TextInput type="number" value={v.rent} onChange={(x) => set({ rent: x })} placeholder="1800" error={errors.rent} data-testid="owner-rent" />
                </Field>
                <Field label={t.form.labels.availableDate}>
                  <DateField value={v.available_date} onChange={(x) => set({ available_date: x })} testId="owner-available-date" futureOnly />
                </Field>
              </div>
              <Field label={t.form.labels.inschrijving}>
                <Pills value={v.inschrijving} onChange={(x) => set({ inschrijving: x })} options={t.form.options.yesNoMaybe} testId="owner-inschrijving" />
              </Field>
              <Field label={t.form.labels.description}>
                <TextArea value={v.description} onChange={(x) => set({ description: x })} />
              </Field>
              <Field label={t.form.labels.photos}>
                <div className="flex flex-wrap gap-3">
                  {v.photos.map((p) => (
                    <div key={p.id} className="relative h-20 w-20 rounded-md overflow-hidden border border-line">
                      <img src={`${api.defaults.baseURL}/files/${p.id}`} alt="" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => setV((prev) => ({ ...prev, photos: prev.photos.filter((x) => x.id !== p.id) }))} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-ink/80 text-paper flex items-center justify-center">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {v.photos.length < 5 && (
                    <label className="h-20 w-20 rounded-md border border-dashed border-line flex flex-col items-center justify-center cursor-pointer hover:border-ink transition-colors text-stone" data-testid="owner-photo-upload">
                      {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} strokeWidth={1.5} />}
                      <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple hidden onChange={(e) => uploadPhotos(e.target.files)} />
                    </label>
                  )}
                </div>
              </Field>
            </>
          )}

          {step === 3 && <ContactStep v={v} set={set} errors={errors} t={t} testPrefix="owner" />}
        </motion.div>
      </AnimatePresence>

      <FormNav step={step} onNext={next} onBack={back} onSubmit={submit} submitting={submitting} t={t} testPrefix="owner" />
    </div>
  );
}

export function StepBar({ step, total = 3 }) {
  return (
    <div className="mb-6">
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? "bg-brand" : "bg-line"}`} />
        ))}
      </div>
    </div>
  );
}

export function ContactStep({ v, set, errors, t, testPrefix, disclosure }) {
  return (
    <>
      <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-stone">{t.form.steps.contact}</h4>
      <Field label={t.form.labels.name} error={errors.name}>
        <TextInput value={v.name} onChange={(x) => set({ name: x })} error={errors.name} data-testid={`${testPrefix}-name`} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t.form.labels.phone} error={errors.phone}>
          <TextInput value={v.phone} onChange={(x) => set({ phone: x })} placeholder="+31 6 12345678" error={errors.phone} data-testid={`${testPrefix}-phone`} />
        </Field>
        <Field label={t.form.labels.email} error={errors.email}>
          <TextInput type="email" value={v.email} onChange={(x) => set({ email: x })} placeholder="ad@ornek.com" error={errors.email} data-testid={`${testPrefix}-email`} />
        </Field>
      </div>
      <Field label={t.form.labels.channel} error={errors.preferred_channel}>
        <Pills value={v.preferred_channel} onChange={(x) => set({ preferred_channel: x })} options={t.form.options.channel} testId={`${testPrefix}-channel`} />
      </Field>
      {/* honeypot */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website || ""} onChange={(e) => set({ website: e.target.value })} className="hidden" aria-hidden="true" />
      <div className="pt-2">
        <Checkbox checked={v.consent} onChange={(x) => set({ consent: x })} error={errors.consent} testId={`${testPrefix}-consent`}>
          {t.form.labels.consent}
        </Checkbox>
        {disclosure && <p className="mt-2 pl-8 text-xs text-stone">{disclosure}</p>}
      </div>
    </>
  );
}

export function FormNav({ step, onNext, onBack, onSubmit, submitting, t, testPrefix }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {step > 1 ? (
        <button type="button" onClick={onBack} className="h-14 px-6 rounded-xl border border-line text-sm font-semibold hover:bg-mist transition-colors" data-testid={`${testPrefix}-back`}>
          {t.form.back}
        </button>
      ) : (
        <span />
      )}
      {step < 3 ? (
        <button type="button" onClick={onNext} className="h-14 px-8 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brandhover shadow-md hover:shadow-lg active:scale-95 transition-[transform,box-shadow,background-color]" data-testid={`${testPrefix}-next`}>
          {t.form.next}
        </button>
      ) : (
        <button type="button" onClick={onSubmit} disabled={submitting} className="h-14 px-8 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brandhover shadow-md hover:shadow-lg active:scale-95 transition-[transform,box-shadow,background-color] disabled:opacity-60 flex items-center gap-2" data-testid={`${testPrefix}-submit`}>
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? t.form.submitting : t.form.submit}
        </button>
      )}
    </div>
  );
}
